import React, { createContext } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useMutation, useQuery, useLazyQuery } from "react-apollo";
import CREATE_ORG_MEDIA from "../../../GraphQl/Mutations/CREATE_ORG_MEDIA";
import UPDATE_MANY_MEDIAS from "../../../GraphQl/Mutations/UPDATE_MANY_MEDIAS";
import GET_ORG_MEDIAS from "../../../GraphQl/Queries/GET_ORG_MEDIAS";
import GET_ORG_MEDIA from "../../../GraphQl/Queries/GET_ORG_MEDIA";
import GET_ADGROUP_CREATIVE from "../../../GraphQl/Queries/GET_ADGROUP_CREATIVE";
import GET_ADGROUP_CREATIVES from "../../../GraphQl/Queries/GET_ADGROUP_CREATIVES";
import GET_ADGROUP_CREATIVES_BY_MEDIA from "../../../GraphQl/Queries/GET_ADGROUP_CREATIVES_BY_MEDIA";
import DELETE_ADGROUP_CREATIVES from "../../../GraphQl/Mutations/DELETE_ADGROUP_CREATIVES";
import CREATE_ADGROUP_CREATIVES from "../../../GraphQl/Mutations/CREATE_ADGROUP_CREATIVES";
import UPDATE_MANY_AD_GROUP_CREATIVES from "../../../GraphQl/Mutations/UPDATE_MANY_AD_GROUP_CREATIVES";
import UPDATE_AD_GROUP_CREATIVES from "../../../GraphQl/Mutations/UPDATE_AD_GROUP_CREATIVES";
import CREATE_MANY_ORG_MEDIA from "../../../GraphQl/Mutations/CREATE_MANY_ORG_MEDIA";
import EDIT_ORG_MEDIA from "../../../GraphQl/Mutations/EDIT_ORG_MEDIA";
import CURRENT_USER from "../../../GraphQl/Queries/CURRENT_USER";

const LibraryCreativesContext = createContext({});
export { LibraryCreativesContext };
export const LibraryCreativesProvider = props => {
  const location = useLocation();
  const history = useHistory();

  const {
    data: { currentUser },
  } = useQuery(CURRENT_USER);

  const loggedInOrg = currentUser && currentUser.role && currentUser.role.org;

  const loggedInOrgId = loggedInOrg.id;
  
  // Node 18 compatible ID extraction from URL pathname with validation
  const extractIdFromPathname = (pathname) => {
    if (!pathname || typeof pathname !== 'string') {
      return null;
    }

    // Normalize pathname: remove trailing slashes, split by '/', filter empty strings
    // Node 18 fix: filter(Boolean) handles empty strings from split more reliably
    const pathSegments = pathname
      .replace(/\/+$/, '') // Remove trailing slashes
      .split('/')
      .filter(segment => segment && segment.trim().length > 0); // More robust filtering for Node 18

    if (pathSegments.length === 0) {
      return null;
    }

    const lastSegment = pathSegments[pathSegments.length - 1];

    // Node 18 compatible regex patterns - more explicit anchoring
    const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
    const numericIdRegex = /^[0-9]+$/;
    const cuidRegex = /^c[a-z0-9A-Z]{24}$/; // CUID format - case insensitive for Node 18

    // Test with trimmed segment to handle encoding differences
    const trimmedSegment = lastSegment.trim();
    if (uuidRegex.test(trimmedSegment) || numericIdRegex.test(trimmedSegment) || cuidRegex.test(trimmedSegment)) {
      return trimmedSegment;
    }

    // If the last segment is not a valid ID, check if it's a route name
    // and look for an ID in the second-to-last segment
    const routeNames = ['details', 'edit', 'view', 'manage'];
    if (routeNames.includes(trimmedSegment.toLowerCase()) && pathSegments.length > 1) {
      const secondToLast = pathSegments[pathSegments.length - 2].trim();
      if (uuidRegex.test(secondToLast) || numericIdRegex.test(secondToLast) || cuidRegex.test(secondToLast)) {
        return secondToLast;
      }
    }

    // Return null for invalid or missing IDs
    return null;
  };

  const pathname = location.pathname;
  const ID = extractIdFromPathname(pathname);

  const [createOrgMedia] = useMutation(CREATE_ORG_MEDIA, {
    variables: {
      orgId: loggedInOrgId,
    },
  });

  const [updateManyMedias] = useMutation(UPDATE_MANY_MEDIAS, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_ORG_MEDIAS,
        variables: { orgId: loggedInOrgId, type: "STILL" },
      },
      {
        query: GET_ORG_MEDIAS,
        variables: { orgId: loggedInOrgId, type: "VIDEO" },
      },
      {
        query: GET_ADGROUP_CREATIVES,
        variables: { orgId: loggedInOrgId },
      },
    ],
  });

  const { refetch: getAdGroupsByMedia } = useQuery(
    GET_ADGROUP_CREATIVES_BY_MEDIA
  );

  const [deleteAdGroupCreatives] = useMutation(DELETE_ADGROUP_CREATIVES, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_ADGROUP_CREATIVES,
        variables: {
          orgId: loggedInOrgId,
        },
      },
    ],
  });

  const [createAdGroupCreatives] = useMutation(CREATE_ADGROUP_CREATIVES, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_ADGROUP_CREATIVES,
        variables: {
          orgId: loggedInOrgId,
        },
      },
    ],
  });

  const [updateManyAdGroupCreatives] = useMutation(
    UPDATE_MANY_AD_GROUP_CREATIVES,
    {
      variables: {
        orgId: loggedInOrgId,
      },
    }
  );

  const [updateAdGroupCreatives] = useMutation(UPDATE_AD_GROUP_CREATIVES, {
    refetchQueries: [
      {
        query: GET_ADGROUP_CREATIVES,
        variables: {
          orgId: loggedInOrgId,
        },
      },
    ],
    errorPolicy: "all",
  });

  const { data: adGroupCreatives, refetch: refetchAdGroupCreatives } = useQuery(
    GET_ADGROUP_CREATIVES,
    {
      //This query is used to get organizational adgroups
      variables: {
        orgId: loggedInOrgId,
      },
    }
  );

  const validateAdGroupCreatives =
    //This filter is used to get adgroups only for the logged in org
    adGroupCreatives &&
    adGroupCreatives.adGroupCreativeses.filter(
      adGroup => adGroup.org.length === 1 || loggedInOrg.parentOrg.length > 0
    );
  // Removed amplify filter - show all ad groups regardless of amplify permissions
  // The UI will handle amplify permissions appropriately

  const assignedAdGroupUnit =
    //This filter is used to get adgroups assigned to media
    adGroupCreatives && ID
      ? adGroupCreatives.adGroupCreativeses.filter(adGroup =>
          adGroup.medias.some(media => media.id === ID)
        )
      : [];
  // Separate queries for display media (STILL) and video media
  const { data: orgDisplayMedias, refetch: refetchMedias } = useQuery(
    GET_ORG_MEDIAS,
    {
      variables: { orgId: loggedInOrgId, type: "STILL" },
    }
  );

  const { data: orgMedia } = useQuery(GET_ORG_MEDIA, {
    variables: {
      mediaId: ID,
      type: "ALL",
    },
    skip: !ID, // Skip query if no valid ID is extracted
  });
  const validateOrgMedias = orgDisplayMedias && orgDisplayMedias.medias;

  const { data: adgroupinfo, refetch: refetchAdGroupInfo } = useQuery(
    GET_ADGROUP_CREATIVE,
    {
      //for individual adgroup information
      variables: {
        adgroupId: ID,
        flag: true,
      },
      skip: !ID, // Skip query if no valid ID is extracted
    }
  );

  const [createManyOrgMedia] = useMutation(CREATE_MANY_ORG_MEDIA, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_ORG_MEDIAS,
        variables: { orgId: loggedInOrgId, type: "STILL" },
      },
      {
        query: GET_ORG_MEDIAS,
        variables: { orgId: loggedInOrgId, type: "VIDEO" },
      },
    ],
  });

  const [updateOrgMedia] = useMutation(EDIT_ORG_MEDIA, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_ORG_MEDIAS,
        variables: { orgId: loggedInOrgId, type: "STILL" },
      },
      { query: GET_ADGROUP_CREATIVES, variables: { orgId: loggedInOrgId } },
    ],
  });

  // Lazy query — only fires when a video tab is visited (reduces initial load from 3 queries to 2)
  const [fetchVideoMedias, { data: orgVideoMedias, refetch: refetchVideoMediasInternal }] = useLazyQuery(
    GET_ORG_MEDIAS,
    {
      variables: { orgId: loggedInOrgId, type: "VIDEO" },
    }
  );

  // Wrapper: fetch on first call, refetch on subsequent calls
  const refetchVideoMedias = orgVideoMedias
    ? refetchVideoMediasInternal
    : fetchVideoMedias;

  // Missing functions that LibraryCreativesView expects
  const setDefaultAmplifyCreatives = async record => {
    try {
      // Clear amplify default from all other ad groups in the org
      const clearDefaultsResult = await updateManyAdGroupCreatives({
        variables: {
          orgId: record.org[0].id,
          isAmplifyDefault: false,
        },
      });

      try {
        // Set the selected ad group as amplify default
        await updateAdGroupCreatives({
          variables: {
            id: record.id,
            data: { isAmplifyDefault: true },
          },
        });

        // Refetch ad groups to update the UI
        await refetchAdGroupCreatives();
      } catch (setDefaultError) {
        // If setting the new default fails, attempt to restore previous state
        // This is a best-effort rollback - in a real transaction system,
        // this would be handled automatically
        try {
          // Note: We don't have the previous default state to restore,
          // so we'll leave all defaults cleared for consistency
          throw new Error(
            `Failed to set amplify default for ad group "${record.name}". Previous defaults have been cleared. Please try again. Original error: ${setDefaultError.message}`
          );
        } catch (rollbackError) {
          throw new Error(
            `Failed to set amplify default and could not restore previous state. Please refresh and try again. Original error: ${setDefaultError.message}`
          );
        }
      }
    } catch (error) {
      // Re-throw with more context if it's not already our custom error
      if (error.message.includes('Failed to set amplify default')) {
        throw error;
      }
      throw new Error(
        `Failed to clear existing amplify defaults for organization. Please try again. Error: ${error.message}`
      );
    }
  };

  // adGroupMedias should be populated from the selected media IDs
  // This will be set by the LibraryCreativesView when creating ad groups
  const adGroupMedias = []; // This gets populated by the view component

  const handleRowClick = record => {
    // Navigate to the appropriate detail page using RESTful pattern
    if (!record || !record.id) {
      return;
    }

    const mediaType = record.type?.toUpperCase();
    const mediaTypePath = mediaType === 'VIDEO' ? 'video' : 'display';

    // Navigate to unit detail page using RESTful pattern
    history.push(`/creatives/${mediaTypePath}/units/${record.id}`);
  };

  return (
    <LibraryCreativesContext.Provider
      value={{
        currentuser: currentUser,
        loggedInOrg,
        loggedInOrgId,
        createOrgMedia,
        updateManyMedias,
        getAdGroupsByMedia,
        deleteAdGroupCreatives,
        createAdGroupCreatives,
        updateManyAdGroupCreatives,
        updateAdGroupCreatives,
        adGroupCreatives: validateAdGroupCreatives,
        orgMedias: validateOrgMedias, // Display media only (STILL type)
        orgMedia,
        createManyOrgMedia,
        refetchMedias,
        updateOrgMedia,
        orgVideoMedias,
        fetchVideoMedias,
        refetchVideoMedias,
        assignedAdGroupUnit,
        adgroupinfo,
        refetchAdGroupInfo,
        // Missing variables now added
        setDefaultAmplifyCreatives,
        adGroupMedias,
        handleRowClick,
        refetchAdGroupCreatives,
      }}
    >
      {props.children}
    </LibraryCreativesContext.Provider>
  );
};
